/**
 * Dashboard widget registry + persistence hook.
 *
 * Each widget has:
 *   - id: stable key (used for localStorage + user_preferences)
 *   - category: grouping for the Personalize dialog
 *   - titleKey: i18n key for the Personalize dialog label
 *   - default: whether visible by default
 *   - colSpan: grid cell span (1..4 on 4-col, 1..2 on 2-col)
 *   - render: component renderer
 *
 * Persistence follows the useColumnConfig pattern:
 *   - localStorage (instant UX) synced with ds.userPreferences (cross-device).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useDataService } from '@/lib/service-context';

// ===== Widget categories =====

export type WidgetCategory =
  | 'overview'
  | 'sales'
  | 'purchases'
  | 'finance'
  | 'checks'
  | 'stock'
  | 'operations'
  | 'withholding';

// ===== Widget definition =====

export interface WidgetDef {
  id: string;
  category: WidgetCategory;
  /** i18n key for Personalize dialog label */
  titleKey: string;
  /** Default visibility when nothing is stored */
  defaultVisible: boolean;
  /** Grid column span on a 4-col layout (1..4). Full-width blocks use 4. */
  colSpan: 1 | 2 | 3 | 4;
  render: () => ReactNode;
}

// ===== Hook =====

const STORAGE_KEY = 'dashboard-widgets-v1';

export function useDashboardWidgets(registry: WidgetDef[]) {
  const ds = useDataService();
  const loaded = useRef(false);
  const registryMap = useRef(new Map(registry.map((w) => [w.id, w])));
  registryMap.current = new Map(registry.map((w) => [w.id, w]));

  const loadLocal = (): string[] | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as string[];
    } catch { /* corrupt — ignore */ }
    return null;
  };

  const computeDefaults = (): string[] =>
    registry.filter((w) => w.defaultVisible).map((w) => w.id);

  /**
   * Fusionne une préférence existante avec les widgets par défaut qui ont
   * été ajoutés au registre APRÈS la sauvegarde. Sans ça, les nouveaux
   * widgets `defaultVisible:true` resteraient invisibles pour tout
   * utilisateur ayant déjà personnalisé son dashboard.
   *
   * Pour identifier les "nouveaux", on compare la liste actuelle à
   * `registry.map(w=>w.id)` pour filtrer les widgets supprimés, et on
   * ajoute les `defaultVisible` encore absents. On sauvegarde ensuite
   * pour que l'alignement soit persisté (pas de boucle).
   */
  const mergeWithDefaults = (saved: string[]): string[] => {
    const validIds = new Set(registry.map((w) => w.id));
    const cleaned = saved.filter((id) => validIds.has(id));
    const missingDefaults = registry
      .filter((w) => w.defaultVisible && !cleaned.includes(w.id))
      .map((w) => w.id);
    return [...cleaned, ...missingDefaults];
  };

  const [visible, setVisible] = useState<string[]>(() => {
    const loaded = loadLocal();
    return loaded ? mergeWithDefaults(loaded) : computeDefaults();
  });

  // Pull from backend once on mount (overrides localStorage if present)
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    ds.userPreferences
      .get(STORAGE_KEY)
      .then((val) => {
        if (val && Array.isArray(val) && val.length > 0) {
          const merged = mergeWithDefaults(val as string[]);
          setVisible(merged);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          // Persiste la fusion côté backend pour que ce rattrapage ne se
          // répète pas à chaque rechargement.
          if (merged.length !== (val as string[]).length) {
            ds.userPreferences.set(STORAGE_KEY, merged).catch(() => {});
          }
        }
      })
      .catch(() => { /* ignore — localStorage fallback is fine */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ds]);

  const save = useCallback(
    (ids: string[]) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
      ds.userPreferences.set(STORAGE_KEY, ids).catch(() => {});
    },
    [ds],
  );

  const toggle = useCallback(
    (id: string) => {
      setVisible((prev) => {
        const next = prev.includes(id)
          ? prev.filter((x) => x !== id)
          : [...prev, id];
        save(next);
        return next;
      });
    },
    [save],
  );

  const showAll = useCallback(() => {
    const all = registry.map((w) => w.id);
    setVisible(all);
    save(all);
  }, [registry, save]);

  const hideAll = useCallback(() => {
    setVisible([]);
    save([]);
  }, [save]);

  const resetDefaults = useCallback(() => {
    const d = computeDefaults();
    setVisible(d);
    save(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save]);

  // Visible widgets keep registry order (not toggle order) for stable layout
  const visibleWidgets = registry.filter((w) => visible.includes(w.id));

  return {
    /** All widgets in registry (for Personalize dialog) */
    allWidgets: registry,
    /** Widgets currently visible in registry order */
    visibleWidgets,
    /** Visibility map for quick lookup */
    isVisible: (id: string) => visible.includes(id),
    toggle,
    showAll,
    hideAll,
    resetDefaults,
  };
}
